import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  Select,
  SelectOption,
  Control,
  Label,
  EmptyState,
  CircularLoader,
  Banner,
  SendIcon,
  Pagination,
  useDocumentTitle,
} from '@forge/common';
import { useJobRuns } from '@/hooks/useJobRuns';
import { usePartners } from '@/hooks/usePartners';
import MockDataBanner from '@/pages/access-management/components/MockDataBanner';

const statusColor: Record<string, 'green' | 'blue' | 'red' | 'gray'> = {
  success: 'green',
  running: 'blue',
  failed: 'red',
};

const JobsPage = () => {
  useDocumentTitle('Jobs — Partner Exchange — OneRing Portal');
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const { partners } = usePartners();
  const { runs, total, isLoading, error, isUsingMockData } = useJobRuns({
    filters: { status: statusFilter || undefined, page, limit },
  });

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const partnerNameById = useMemo(
    () => new Map(partners.map((p) => [p.id, p.displayName])),
    [partners],
  );

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-1">Jobs</h2>
      <p className="text-sm text-color-tertiary mb-4">
        Every scheduled partner transfer run, triggered by onering-crons' <code>sftp-transfers.yml</code> workflow.
      </p>

      {isUsingMockData && (
        <div className="mb-4">
          <MockDataBanner featureName="Jobs" />
        </div>
      )}

      {error ? (
        <Banner
          variant="inline"
          type="error"
          title="Failed to load job runs"
          description={error}
          canClose={false}
        />
      ) : (
        <Card>
          <CardHeader
            title="Job Runs"
            subtitle={`${total} run${total !== 1 ? 's' : ''}`}
            slotRight={
              <Control hideSpacing="bottom" className="w-48">
                <Label htmlFor="job-status-filter">Status</Label>
                <Select
                  id="job-status-filter"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <SelectOption value="" label="All">
                    All
                  </SelectOption>
                  <SelectOption value="success" label="Success">
                    Success
                  </SelectOption>
                  <SelectOption value="failed" label="Failed">
                    Failed
                  </SelectOption>
                  <SelectOption value="running" label="Running">
                    Running
                  </SelectOption>
                </Select>
              </Control>
            }
          />
          <CardContent>
            {isLoading ? (
              <div className="flex min-h-[240px] items-center justify-center">
                <CircularLoader size="large" />
              </div>
            ) : runs.length === 0 ? (
              <EmptyState icon={<SendIcon />} description="No job runs found" />
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Job</TableCell>
                      <TableCell>Partner</TableCell>
                      <TableCell>Direction</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell>Files</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {runs.map((run) => (
                      <TableRow
                        key={run.id}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                        onClick={() => run.partnerId && navigate(`/partners/${run.partnerId}`)}
                      >
                        <TableCell>
                          <span className="font-mono text-xs">{run.jobName}</span>
                        </TableCell>
                        <TableCell>
                          {run.partnerId ? partnerNameById.get(run.partnerId) ?? run.partnerId : '—'}
                        </TableCell>
                        <TableCell>{run.direction}</TableCell>
                        <TableCell>
                          <Badge color={statusColor[run.status] ?? 'gray'}>{run.status}</Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-color-secondary">
                            {new Date(run.startedAt).toLocaleString()}
                          </span>
                        </TableCell>
                        <TableCell>{run.filesProcessed}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Pagination
                  currentPage={page}
                  totalItems={total}
                  totalPages={totalPages}
                  pageSize={limit}
                  pageSizeOptions={[25, 50, 100]}
                  showPageInfo
                  showPageSizeList
                  onPageChange={setPage}
                  onPageSizeChange={(newSize) => {
                    setPage(1);
                    setLimit(newSize);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default JobsPage;
